<template>
  <cs-preloader-overlay :loading="loading">
    <div ref="mainContainer" class="main-container">
      <div class="content-container">
        <navigation-tabs />
        <template v-if="isEmptyPartnerList">
          <partnership-welcome-screen>
            <partnership-settings
              :partner="partner"
              :projects="personalProjectList"
              @update-partner="onUpdatePartner"
              @change-rate="onChangePartnerRate"
              @load-next="loadNextPageProjects"
            />
          </partnership-welcome-screen>
        </template>
        <template v-else>
          <div class="partnership-settings-container">
            <partnership-settings
              :partner="partner"
              :projects="personalProjectList"
              @update-partner="onUpdatePartner"
              @change-rate="onChangePartnerRate"
              @load-next="loadNextPageProjects"
            />
          </div>
          <project-layout>
            <partner-project-card
              v-for="project in partner.childProjects"
              :key="project.projectId"
              :project="project"
              @disable-project="onShownDisableConfirmation"
              @transaction-browsing="onShownPermissionDialogByStatus"
            />
          </project-layout>
          <img
            v-if="loadingPush"
            src="@/assets/img/loader.svg"
          />
        </template>
      </div>

      <cs-dialog v-model="isShownDisableConfirmation">
        <dialog-default-layout>
          <h2 slot="title">{{ $t('you.want.turn.off.project.from.partners.program') }}</h2>
          <div slot="footer" class="confirmation-control-buttons">
            <cs-button :width="190" @click="onDisablePartner">
              {{ $t('common.yes') }}
            </cs-button>
            <cs-button type="transparent" @click="onCloseDisableConfirmation">
              {{ $t('common.cancel') }}
            </cs-button>
          </div>
        </dialog-default-layout>
      </cs-dialog>

      <cs-dialog v-model="isShownConfirmChangePermission">
        <dialog-default-layout>
          <h2 slot="title">{{ $t('have.not.access.to.partner.transactions') }}</h2>
          <div slot="footer" class="confirmation-control-buttons">
            <cs-button :width="190" @click="onChangeTransactionsBrowsingPermission">
              {{ $t('common.yes') }}
            </cs-button>
            <cs-button type="transparent" @click="onCloseChangePermission">
              {{ $t('common.cancel') }}
            </cs-button>
          </div>
        </dialog-default-layout>
      </cs-dialog>

      <cs-dialog v-model="isShownPermissionPending">
        <dialog-default-layout>
          <h2 slot="title">{{ $t('email.with.request.sent.to.partner') }}</h2>
          <div slot="footer">
            <cs-button :width="190" @click="onCloseChangePermission">Ok</cs-button>
          </div>
        </dialog-default-layout>
      </cs-dialog>
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';

import NavigationTabs from 'organisms/reports/components/NavigationTabs';
import ProjectLayout from 'organisms/reports/components/ProjectLayout';
import PartnerProjectCard from './components/PartnerProjectCard';
import PartnershipSettings from './components/PartnershipSettings';
import PartnershipWelcomeScreen from './components/PartnershipWelcomeScreen';
import DialogDefaultLayout from 'modules/Dialogs/DefaultLayout';

import Partnership from 'services/partner/Partnership';

import {
  fetch as fetchPartner,
  update as updatePartner,
  disable as disablePartner,
  changeRate as changePartnerRate,
  createPermission as createTransactionBrowsingPermission
} from 'services/partner';

import { EVENT_BUS } from '@/eventBus';
import { TRANSACTION_BROWSING_APPROVAL } from '@/const';
import { CABINET } from '@/router/const';

import { PENDING as PENDING_STATUS } from './const';
import HTTPService from '@/services/Http';

export default {
  name: 'Partnership',
  components: {
    NavigationTabs,
    ProjectLayout,
    PartnerProjectCard,
    PartnershipSettings,
    PartnershipWelcomeScreen,
    DialogDefaultLayout,
  },
  async created() {
    this.loading = true;

    this.partner = new Partnership;
    this.partner.setData(await fetchPartner());
    await this.loadNextPage();
    await this.loadNextPageProjects();

    EVENT_BUS.$on(TRANSACTION_BROWSING_APPROVAL, data => {
      this.partner.setBrowsingPermission(data.childProjectId, data.status);

      this.$alert({
        type: 'success',
        text: this.$t('access.granted'),
      });
    });

    this.loading = false;
  },
  mounted() {
    window.addEventListener('scroll', this.scrollHandler);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.scrollHandler);
  },
  data() {
    return {
      loading: false,
      loadingPush: false,
      pageSize: 20,
      isEndPage: false,
      page: 0,
      loadingPushProjects: false,
      pageSizeProjects: 20,
      isEndPageProjects: false,
      pageProjects: 0,
      partner: null,
      selectedProject: null,
      isShownDisableConfirmation: false,
      isShownConfirmChangePermission: false,
      isShownPermissionPending: false,
      personalProjectList: [],
    };
  },
  computed: {
    isEmptyPartnerList() {
      return this.$validate.isEmptyArray(this.partner.childProjects);
    },
    isActiveReferralProgram() {
      return this.partner.isActive();
    },
    ...mapGetters(['PROJECT_LIST']),
  },
  methods: {
    ...mapMutations([
      'SET_SCROLL_STATES',
    ]),

    async loadNextPage() {
      if (this.loadingPush || this.isEndPage) {
        return;
      }
      this.loadingPush = true;
      try {
        this.page++;
        const projectList = await HTTPService.getPartnerProjects(this.page);
        if (this.page === 1) {
          this.partner.setChildProjects(projectList);
        } else {
          this.partner.pushChildProjects(projectList);
        }
        this.isEndPage = projectList.length < this.pageSize;
      } finally {
        this.loadingPush = false;
      }
    },

    async loadNextPageProjects() {
      if (this.loadingPushPartners || this.isEndPagePartners) {
        return;
      }
      this.loadingPushPartners = true;
      try {
        this.pageProjects++;
        const projectList = await HTTPService.getPersonalProjects(this.pageProjects);

        if (this.pageProjects === 1) {
          this.personalProjectList = projectList;
        } else {
          this.personalProjectList.push(...projectList);
        }
        this.isEndPagePartners = projectList.length < this.pageSize;
      } finally {
        this.loadingPushPartners = false;
      }
    },

    scrollHandler() {
      let scrollTop = document.documentElement.scrollTop || document.body.scrollTop || window.pageYOffset || 0;
      let offset = scrollTop + window.innerHeight;
      let height = document.documentElement.offsetHeight;

      // load and push transactions when reaches bottom
      let heightFactor = 100;
      if (offset + heightFactor >= height && !this.loadingPush) {
        this.loadNextPage();
      }

      // filters block
      let arrangerRect = this.$refs.mainContainer.getBoundingClientRect();
      let arrangerOffset = arrangerRect.top;

      this.SET_SCROLL_STATES({
        type: 'Partnership_block_is_fixed',
        params: arrangerOffset <= 5,
      });
    },

    async startLoad() {
      this.page = 0;
      this.isEndPage = false;
      await this.loadNextPage();
    },

    async onUpdatePartner(project) {
      const responseData = await updatePartner({ parentProjectId: project.id });
      responseData.childProjects = null;
      this.partner.setData(responseData);
      this.partner.updateData({
        parentProjectId: project.id,
        parentProjectName: project.name,
      });
      await this.startLoad();
    },
    onShownPermissionDialogByStatus(project) {
      if (project.transactionsBrowsingPermission) {
        // TODO: @danealton отрефакторить метод goTo, по сути тут смена проекта, а не переход в кабинет
        project.goTo(CABINET);
        return;
      }

      this.selectedProject = project;

      if (project.transactionBrowsingRequestStatus === PENDING_STATUS) {
        this.isShownPermissionPending = true;
      } else {
        this.isShownConfirmChangePermission = true;
      }
    },
    onCloseChangePermission() {
      this.selectedProject = null;
      this.isShownPermissionPending = false;
      this.isShownConfirmChangePermission = false;
    },
    async onChangeTransactionsBrowsingPermission() {
      const responseData = await createTransactionBrowsingPermission(this.selectedProject.id);
      const childProjects = responseData.childProjects;
      delete responseData.childProjects;
      this.partner.setData(responseData);
      this.partner.mergeChild(childProjects);
      if (this.selectedProject.transactionBrowsingRequestStatus !== PENDING_STATUS) {
        this.partner.setBrowsingPermission(this.selectedProject.id, PENDING_STATUS);
        this.isShownConfirmChangePermission = false;
        this.isShownPermissionPending = true;
      }
      this.onCloseChangePermission();
    },
    onShownDisableConfirmation(project) {
      this.selectedProject = project;
      this.isShownDisableConfirmation = true;
    },
    onCloseDisableConfirmation() {
      this.selectedProject = null;
      this.isShownDisableConfirmation = false;
    },
    async onDisablePartner() {
      await disablePartner(this.selectedProject.id);
      this.partner.removeFromChildProjects(this.selectedProject.id);
      this.onCloseDisableConfirmation();
    },
    async onChangePartnerRate(rate) {
      try {
        await changePartnerRate({
          rate,
          parentProjectId: this.partner.parentProjectId,
        });

        this.partner.setRate(rate);
      } catch {
        this.$alert({
          type: 'warning',
          text: this.$t('change.rate.failed'),
        });
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .main-container
    position: relative
    overflow: hidden

    +respond-to('medium')
      margin-top: -100px

  .content-container
    width: 100%
    max-width: 620px
    margin: 0 auto
    position: relative
    z-index: 2000

  .partnership-settings-container
    margin-bottom: 20px

    +respond-to('small')
      margin-top: 20px
      margin-bottom: 0

  .confirmation-control-buttons
    display: flex
    flex-direction: column
    align-items: center
    justify-content: space-between
    padding-top: 20px

    *
      &:not(:last-child)
        margin-bottom: 20px

    +respond-to('small')
      flex-direction: row

      *
        &:not(:last-child)
          margin-bottom: 0
</style>
